import { useRef, useState, useEffect, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName } from "@/theory/melody";
import { spellDegree } from "@/theory/spell";
import { kickAudio } from "@/audio/piano";
import { playRhythm, setRhythmBpm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 18 — Blues with Left Hand Bass Patterns.
 *
 * What the video teaches (and Jason's handwritten pages for it): a major-key blues
 * on the three dominant sevenths — C7, F7, G7 in the video — with the right hand
 * holding the chord (or playing the Charleston) while the left hand walks
 *   1  3  5  6 | ♭7  6  5  3        "1, 3, 5, 6, 7 flat, 6, 5, 3"
 * two bars per chord. His page writes it out: C E G A | B♭ A G E over C7,
 * F A C D | E♭ D C A over F7, G B D E | F E D B over G7.
 *
 * (This page previously taught a minor-key walk — root, MINOR third, fifth, sixth —
 * over minor triads in A minor. Neither the video nor his notes do that.)
 */

/** The two-bar walk, in semitones above the chord root. */
const WALK = [0, 4, 7, 9, 10, 9, 7, 4];
const WALK_DEGREES = ["1", "3", "5", "6", "♭7", "6", "5", "3"];

/** I, IV and V of the key as dominant sevenths. */
const CHORDS = [
  { roman: "I7", degree: "1", semis: 0 },
  { roman: "IV7", degree: "4", semis: 5 },
  { roman: "V7", degree: "5", semis: 7 },
] as const;

/** The 12-bar blues from his page: I7 ×4 · IV7 IV7 I7 I7 · V7 IV7 I7 I7. */
const TWELVE_BAR = [0, 0, 0, 0, 1, 1, 0, 0, 2, 1, 0, 0];

type RightHand = "hold" | "charleston";

export default function PianoIntermediate18() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "18");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="G" availableQualities={["major"]}>
        <PIShell
          title="Blues with Left Hand Bass Patterns"
          exerciseGoal="A walking bass under the blues: 1, 3, 5, 6, ♭7, 6, 5, 3 — two bars on every chord — while the right hand holds the dominant seventh or plays the Charleston."
          bpm={84}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const [bpm, setBpm] = useState(84);
  const [rh, setRh] = useState<RightHand>("hold");
  const [running, setRunning] = useState<"walk" | "twelve" | null>(null);
  const [chordIdx, setChordIdx] = useState(0);
  const [step, setStep] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);

  const rootPc = (i: number) => (k.tonicPc + CHORDS[i].semis) % 12;
  /** Bass root between E2 and D♯3; the chord sits around middle C. */
  const bassRoot = (i: number) => 40 + ((rootPc(i) - 4 + 12) % 12);
  const chordRoot = (i: number) => 54 + ((rootPc(i) - 6 + 12) % 12);
  const rootName = (i: number) => spellDegree(k.tonicName, CHORDS[i].degree) ?? "";
  const chordName = (i: number) => `${rootName(i)}7`;
  /** Spelled from the CHORD's root, so C7's seventh reads B♭ even in a sharp key. */
  const spellOn = (i: number, degrees: readonly string[]) => degrees.map((d) => spellDegree(rootName(i), d) ?? d);
  const pitch = (m: number) => midiToPitchName(m, k.useFlats);

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    setRunning(null);
    setStep(-1);
  };
  useEffect(() => () => handleRef.current?.stop(), []);
  useEffect(() => { if (running) setRhythmBpm(bpm, 0.05); }, [bpm, running]);
  useEffect(() => { stop(); }, [k.tonicPc, rh]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Bars → eighth-note events. A chord lasting one bar walks the first half only. */
  const build = (bars: number[]) => {
    const events: { beat: number; notes: string[]; durationSteps: number; velocity: number }[] = [];
    bars.forEach((ci, bar) => {
      const sameAsPrev = bar > 0 && bars[bar - 1] === ci && (bar % 2 === 1);
      const half = sameAsPrev ? 4 : 0; // second bar of a two-bar walk
      for (let q = 0; q < 4; q++) {
        events.push({ beat: bar * 8 + q * 2, notes: [pitch(bassRoot(ci) + WALK[half + q])], durationSteps: 2, velocity: q === 0 ? 0.85 : 0.68 });
      }
      const chord = [0, 4, 7, 10].map((o) => pitch(chordRoot(ci) + o));
      if (rh === "hold") events.push({ beat: bar * 8, notes: chord, durationSteps: 8, velocity: 0.55 });
      else {
        // The Charleston: beat 1 and the "and" of 2.
        events.push({ beat: bar * 8, notes: chord, durationSteps: 3, velocity: 0.62 });
        events.push({ beat: bar * 8 + 3, notes: chord, durationSteps: 2, velocity: 0.58 });
      }
    });
    return events;
  };

  const playWalk = async (ci: number) => {
    kickAudio();
    stop();
    setChordIdx(ci);
    handleRef.current = await playRhythm(build([ci, ci]), {
      bpm, subdivide: 2, totalSteps: 16, loop: true, click: true, beatsPerBar: 4, onStep: (s) => setStep(s),
    });
    setRunning("walk");
  };
  const playTwelve = async () => {
    kickAudio();
    stop();
    handleRef.current = await playRhythm(build(TWELVE_BAR), {
      bpm, subdivide: 2, totalSteps: 12 * 8, loop: true, click: true, beatsPerBar: 4,
      onStep: (s) => { setStep(s); setChordIdx(TWELVE_BAR[Math.floor(s / 8)]); },
    });
    setRunning("twelve");
  };

  const walkStep = running === "walk" && step >= 0 ? Math.floor(step / 2) % 8 : -1;
  const highlights: KeyHighlight[] = [0, 4, 7, 9, 10].map((o, i) => ({
    pc: (rootPc(chordIdx) + o) % 12,
    tone: i === 0 ? ("root" as const) : ("voice" as const),
    label: ["1", "3", "5", "6", "♭7"][i],
  }));

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <p className="text-[12.5px] text-cyan-300/80">
          Jason plays this in C in the video (C7, F7, G7). The player starts in G — pick C to play along.
        </p>
        <PianoDiagram highlights={highlights} octaves={2} startOctave={2} playable />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Lit: the walk's notes over {chordName(chordIdx)} — 1, 3, 5, 6 and ♭7.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The right hand" title="Dominant sevenths — the sound of the blues">
          <p>
            A dominant seventh is a major chord with the minor seventh above its root added:
            {" "}{chordName(0)} = {spellOn(0, ["1", "3", "5", "♭7"]).join(" ")}.
            Build one on each of the three chords of the blues — {chordName(0)}, {chordName(1)} and {chordName(2)}.
            The right hand can simply hold the chord, keep a pulse, or play the Charleston rhythm.
            Plain triads work too while you're learning.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "I would encourage you to learn the dominant seventh as that's the essence of the blues."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The walking bass" title="1, 3, 5, 6, ♭7, 6, 5, 3">
          <PIQuote attribution="Jason · lesson video">
            "Just a walking bass. Let me count out the intervals. 1, 3, 5, 6, 7 flat, 6, 5, 3."
          </PIQuote>
          <p>
            Up the chord to the sixth in the first bar, then down from the flat seventh in the
            second — two bars on each chord. In the key of {k.tonicName}:
          </p>
          <WalkTable
            rows={[0, 1, 2].map((ci) => ({
              chord: chordName(ci),
              notes: spellOn(ci, WALK_DEGREES),
              active: running === "walk" && chordIdx === ci,
              onPlay: () => (running === "walk" && chordIdx === ci ? stop() : playWalk(ci)),
            }))}
            activeStep={walkStep}
          />
          <PIInsight>
            The ♭7 is the note that makes it the blues: it belongs to the dominant seventh chord,
            not to the major scale. Fingering tip from the video: cross the index finger over after
            the thumb on the sixth.
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The 12-bar blues" title="Put the walk under the whole form">
          <div className="my-3 grid grid-cols-4 gap-1.5">
            {TWELVE_BAR.map((ci, bar) => {
              const now = running === "twelve" && Math.floor(step / 8) === bar;
              return (
                <div key={bar} className={`rounded-lg border px-2 py-2 text-center ${now ? "border-amber-300 bg-amber-500/25" : "border-cyan-400/20 bg-zinc-800/40"}`}>
                  <div className="font-mono text-[10px] text-cyan-300/60">bar {bar + 1}</div>
                  <div className="text-[15px] font-bold text-cyan-50">{chordName(ci)}</div>
                </div>
              );
            })}
          </div>
          <p className="text-[13px] text-cyan-100/80">
            Where a chord lasts two bars the bass walks all eight notes; where it lasts one bar
            (bars 9 and 10) it walks the first four. In the video, in C, that one-bar chord is G:
            "For G, you can't do a lot of it because it's one cycle."
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={() => (running === "twelve" ? stop() : playTwelve())}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              {running === "twelve" ? "■ Stop" : "▶ Play the 12-bar blues"}
            </button>
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] text-cyan-300/70">Right hand:</span>
              {(["hold", "charleston"] as RightHand[]).map((r) => (
                <button
                  key={r}
                  onClick={() => setRh(r)}
                  className={`rounded-md border px-2.5 py-1.5 font-mono text-[11px] font-bold uppercase ${
                    rh === r ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-cyan-400/30 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
                  }`}
                >
                  {r === "hold" ? "Hold the chord" : "Charleston"}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-[12px] text-cyan-200">
              <span className="font-mono uppercase tracking-wider text-cyan-300/80">Tempo</span>
              <input type="range" min={60} max={120} step={2} value={bpm} onChange={(e) => setBpm(Number(e.target.value))} className="w-32 accent-amber-400" />
              <span className="w-10 font-mono text-[11px] text-cyan-100">{bpm}</span>
            </label>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Practice ladder" title="Twelve minutes, full blues">
          <PracticeLadder
            intro={<>Get the left hand automatic first — then the right hand, then your voice. Jason sings blues songs like "I'm Tore Down" and "Sweet Home Chicago" over this bass.</>}
            rungs={[
              { title: "LH walk on each chord", minutes: 4, accent: "amber", detail: "The two-bar walk on I7, IV7 and V7, one at a time, until it runs by itself." },
              { title: "Add the RH chord", minutes: 4, accent: "cyan", detail: "Hold the dominant seventh for each bar, then try the Charleston." },
              { title: "The 12-bar form, and sing", minutes: 4, accent: "rose", detail: "Play the whole form and sing a blues melody over it." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke: the walk written out, one row per chord ───────────────── */
function WalkTable({
  rows,
  activeStep,
}: {
  rows: { chord: string; notes: string[]; active: boolean; onPlay: () => void }[];
  activeStep: number;
}) {
  return (
    <div className="my-4 overflow-x-auto rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <div className="grid min-w-[520px] grid-cols-[72px_repeat(8,minmax(0,1fr))] gap-1">
        <div />
        {WALK_DEGREES.map((d, i) => (
          <div key={i} className={`text-center font-mono text-[11px] text-cyan-300/70 ${i === 4 ? "border-l border-cyan-400/30" : ""}`}>{d}</div>
        ))}
        {rows.map((r) => (
          <FragmentRow key={r.chord} row={r} activeStep={activeStep} />
        ))}
      </div>
    </div>
  );
}

function FragmentRow({ row, activeStep }: { row: { chord: string; notes: string[]; active: boolean; onPlay: () => void }; activeStep: number }) {
  return (
    <>
      <button
        onClick={row.onPlay}
        className={`rounded-lg border px-2 py-2 text-[13px] font-bold ${row.active ? "border-amber-300 bg-amber-500/20 text-amber-100" : "border-cyan-400/30 bg-cyan-500/5 text-cyan-100 hover:bg-cyan-500/15"}`}
      >
        {row.active ? "■" : "▶"} {row.chord}
      </button>
      {row.notes.map((n, i) => (
        <div
          key={i}
          className={`flex items-center justify-center rounded-md border py-2 text-[14px] font-semibold ${i === 4 ? "ml-1" : ""} ${
            row.active && activeStep === i ? "border-amber-300 bg-amber-400/40 text-amber-50" : "border-cyan-400/15 bg-zinc-800/40 text-cyan-50"
          }`}
        >
          {n}
        </div>
      ))}
    </>
  );
}

/* ── Shared helpers ───────────────────────────────────────────────── */
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between"
      >
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
            ✦ Study deck
          </div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{ overflow: "hidden" }}
      >
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote
        className="text-[17px] leading-relaxed text-cyan-50/95"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}
      >
        {children}
      </blockquote>
      {attribution && (
        <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">
          — {attribution}
        </figcaption>
      )}
    </figure>
  );
}
